import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { defaultRouteFor } from "@/lib/roles";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Sparkle, ChartLineUp, ArrowRight, Lock, Rocket } from "@phosphor-icons/react";

const BG_URL =
  "https://static.prod-images.emergentagent.com/jobs/6a836a56-ffeb-4913-9fed-b4369b33717e/images/7df1764f7501159fc5c7d5992f88795c588b6f89d8ece8b1bdfc43769f3312e2.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [bootstrapName, setBootstrapName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/needs-bootstrap");
        setNeedsBootstrap(!!r?.data?.needs_bootstrap);
      } catch {
        // ignore — login still works
      }
    })();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome, ${user.name}`);
      navigate(defaultRouteFor(user));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemo = async () => {
    setEmail("admin@auditfirm.com");
    setPassword("Admin@123");
    setSubmitting(true);
    try {
      const user = await login("admin@auditfirm.com", "Admin@123");
      toast.success(`Welcome, ${user.name}`);
      navigate(defaultRouteFor(user));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Quick login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const fillClientDemo = async () => {
    setEmail("lalit.tandon@swageazy.com");
    setPassword("Lalit@123");
    setSubmitting(true);
    try {
      const user = await login("lalit.tandon@swageazy.com", "Lalit@123");
      toast.success(`Welcome, ${user.name}`);
      navigate(defaultRouteFor(user));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Quick login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBootstrap = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/register-audit", { email, name: bootstrapName, password });
      toast.success("First admin created. Signing you in...");
      const user = await login(email, password);
      navigate(defaultRouteFor(user));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bootstrap failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative w-1/2 bg-[#0B2A5B] text-white overflow-hidden">
        <img
          src={BG_URL}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B2A5B]/85 via-[#0B2A5B]/75 to-[#1E40AF]/60" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="bg-white rounded-md p-4 inline-flex items-center self-start shadow-sm">
            <img src="/brand/delhigence-logo-sm.png" alt="DELHiGENCE.AI" className="h-12 w-auto" />
          </div>

          <div className="max-w-md">
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-200 text-[11px] mb-4">
              <Sparkle size={12} weight="fill" /> AI-guided audit response, built for your team
            </div>
            <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-tight" style={{ fontFamily: "Geist" }}>
              Resolve audit observations. <span className="text-blue-300">Reduce your business risk.</span>
            </h1>
            <p className="text-slate-300 mt-4 leading-relaxed">
              Review the observations raised on your business, attach evidence, respond to your auditors, and watch
              your risk score drop in real time — with an AI advisor guiding every step.
            </p>

            <div className="grid grid-cols-3 gap-4 mt-8">
              {[
                { k: "Clear Findings", v: "See every observation in one place" },
                { k: "Respond & Resolve", v: "Attach proof, submit resolutions" },
                { k: "AI Improvement Plan", v: "30 / 60 / 90 day actions" },
              ].map((f) => (
                <div key={f.k} className="rounded-md border border-white/10 bg-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{f.k}</div>
                  <div className="text-xs text-slate-200 mt-0.5">{f.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-slate-400">
            <ChartLineUp size={14} className="inline mr-1" />
            Trusted by audit teams and the businesses they partner with.
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/brand/delhigence-logo-sm.png" alt="DELHiGENCE.AI" className="h-10 w-auto" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Geist" }}>
            {needsBootstrap ? "Set up your first admin" : "Sign in to your console"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {needsBootstrap
              ? "This environment is empty. Create the first audit admin to get started — additional accounts can be invited from inside the console."
              : "For clients responding to audit observations and audit teams managing them."}
          </p>

          {needsBootstrap ? (
            <form onSubmit={handleBootstrap} className="space-y-4 mt-6" data-testid="bootstrap-form">
              <div>
                <Label htmlFor="bs-name">Full Name</Label>
                <Input
                  id="bs-name"
                  value={bootstrapName}
                  onChange={(e) => setBootstrapName(e.target.value)}
                  required
                  placeholder="Shubham Garg"
                  data-testid="bootstrap-name-input"
                />
              </div>
              <div>
                <Label htmlFor="bs-email">Email</Label>
                <Input
                  id="bs-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@auditfirm.com"
                  data-testid="bootstrap-email-input"
                />
              </div>
              <div>
                <Label htmlFor="bs-password">Password</Label>
                <Input
                  id="bs-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="At least 6 characters"
                  data-testid="bootstrap-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={submitting}
                data-testid="bootstrap-submit-btn"
              >
                <Rocket size={14} className="mr-1" />
                {submitting ? "Creating..." : "Create Admin & Sign In"}
              </Button>
              <p className="text-[11px] text-slate-500">
                After signing in, head to <span className="font-semibold">Settings → Data Tools</span> to import your preview snapshot.
              </p>
            </form>
          ) : (
          <>
          <form onSubmit={handleLogin} className="space-y-4 mt-6" data-testid="login-form">
            <div>
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@firm.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={submitting}
              data-testid="login-submit-btn"
            >
              {submitting ? "Signing in..." : "Sign in"}
              <ArrowRight size={16} className="ml-1" />
            </Button>
            {(typeof window !== "undefined" &&
              (window.location.hostname.includes("preview.emergentagent.com") ||
                window.location.hostname === "localhost")) && (
              <>
                <button
                  type="button"
                  onClick={fillDemo}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-dashed border-blue-300 bg-blue-50/50 hover:bg-blue-50 text-[#0B2A5B] text-xs font-medium flex items-center justify-center gap-1.5 transition"
                  data-testid="preview-quick-login-btn"
                  title="Available in preview only — hidden on production"
                >
                  <Sparkle size={12} weight="fill" /> Preview Quick Login — Audit Admin
                </button>
                <button
                  type="button"
                  onClick={fillClientDemo}
                  className="w-full mt-2 px-3 py-2 rounded-md border border-dashed border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50 text-emerald-800 text-xs font-medium flex items-center justify-center gap-1.5 transition"
                  data-testid="preview-quick-login-client-btn"
                  title="Available in preview only — hidden on production"
                >
                  <Sparkle size={12} weight="fill" /> Preview Quick Login — Client (Lalit)
                </button>
              </>
            )}
          </form>

          <div
            className="mt-6 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-[11px] text-slate-600"
            data-testid="auditor-invite-only-note"
          >
            <Lock size={12} className="mt-0.5 text-slate-500 shrink-0" />
            <div>
              <span className="font-semibold text-slate-700">Auditor accounts are invite-only.</span>{" "}
              Contact your audit administrator to receive access. Client logins are created from the audit console.
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
